import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Flex,
  Input,
  Space,
  Table,
  message,
} from 'antd';
import {
  DownloadOutlined,
  HomeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  colourPalette,
  dateFormat,
  filterTimeout,
  notificationTimeout,
  notificationType,
  query100,
  scrollHeight,
} from '../common/constants';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db } from '../firebase';
import './style.scss';

export default function ResidentAccessRecord({ site }) {
  const [data, setData] = useState([]);
  const [filterDate, setFilterDate] = useState({ end: '', start: '' }); // date filter
  const [loading, setLoading] = useState(false);
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  const fileDate = (date) =>
    date !== '' ? `_${date.split('-').join('')}` : '';
  const filename = `vmsResident${fileDate(filterDate.start)}${fileDate(filterDate.end)}.csv`;
  const headers = [
    { label: 'Date Time', key: 'dateTime' },
    { label: 'Block', key: 'blockNumber' },
    { label: 'Unit', key: 'unitNumber' },
    { label: 'Resident Name', key: 'residentName' },
    { label: 'Door Name', key: 'doorName' },
    { label: 'Contact Number', key: 'contactNumber' },
  ];
  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'record')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { highlighted, searchActive } = colourPalette;
  const { timeoutInfo, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeInfo, typeSuccess, typeWarning } = notificationType;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchRecords = useCallback(
    (dateFilter, siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      const records = [];
      const accessRef = collection(db, 'residentAccessRecords');
      let q = query(accessRef, where('siteId', '==', siteId));

      // add date filters if applicable, otherwise add query limit
      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('accessDate', '>=', dateFilter.start),
          where('accessDate', '<=', dateFilter.end),
        );
      } else {
        q = query(q, limit(query100));
      }

      // order query results
      q = query(q, orderBy('accessDate', 'desc'));

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const record = {};
            const dict = doc.data();

            record.blockNumber = dict.blockNumber;
            record.contactNumber = dict.contactNumber;
            record.dateTime = `${dict.accessDate} ${dict.accessTime}`;
            record.doorName = dict.doorName;
            record.residentName = dict.residentName;
            record.unitNumber = dict.unitNumber;

            records.push(record);
          });

          if (records?.length) {
            openMessage(
              `fetching latest ${records.length} ${pluralise(records.length, 'record')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(records);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
    ],
  );

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleReload = () => {
    fetchRecords(filterDate, id);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Date Time',
      dataIndex: 'dateTime',
      width: 120,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.dateTime, b.dateTime),
        multiple: 1,
      },
    },
    {
      title: 'Block',
      dataIndex: 'blockNumber',
      width: 100,
      ...getColumnSearchProps('blockNumber'),
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      width: 80,
      ...getColumnSearchProps('unitNumber'),
    },
    {
      title: 'Resident Name',
      dataIndex: 'residentName',
      width: 200,
      ...getColumnSearchProps('residentName'),
    },
    {
      title: 'Door Name',
      dataIndex: 'doorName',
      width: 200,
    },
    {
      title: 'Contact Number',
      dataIndex: 'contactNumber',
      width: 160,
    },
  ];

  useEffect(() => fetchRecords(filterDate, id), [fetchRecords, filterDate, id]);

  return (
    <>
      <SEO
        description="Review resident access records"
        name="qiwei"
        title="Access Records (Resident)"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Access Records (Resident)</span>,
            },
          ]}
        />
        <Flex align="center" wrap>
          <RangePicker
            format={dateFormat}
            id="rangePicker"
            name="rangePicker"
            onChange={handleDatePickerChange}
            showNow
            variant="borderless"
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleReload}
            type="link"
          >
            Reload
          </Button>
          <CSVLink
            className="csv download-button"
            data={data}
            enclosingCharacter=""
            filename={filename}
            headers={headers}
          >
            <DownloadOutlined />
            <span className="text">Download Report (CSV)</span>
          </CSVLink>
        </Flex>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="dateTime"
        scroll={{ x: 860, y: scrollHeight }}
      />
    </>
  );
}

ResidentAccessRecord.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
